import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Badge, Input, Alert, Modal, message, Typography } from 'antd';
import { SearchOutlined, EyeOutlined, DeleteOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './SupportRequests.css';

const { Paragraph, Text } = Typography;

const SupportRequests = () => {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const navigate = useNavigate();

  const fetchApprovedRecords = async () => {
    setLoading(true);
    try {
      console.log('Envoi de la requête vers /backend/admin_get_approved_support_records.php');
      const response = await fetch('/backend/admin_get_approved_support_records.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('Statut HTTP:', response.status);
      const text = await response.text();
      console.log('Réponse brute:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Données parsées:', data);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        setDataSource(data);
        setFilteredData(data);
        setError(null);
      } else {
        if (response.status === 403) {
          console.log('Erreur 403: Session expirée');
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/adminlogin');
        } else {
          setError(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur de réseau:', error.message);
      setError(error.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceActivities = async () => {
    setLoading(true);
    try {
      const url = '/backend/admin_get_service_activities.php';
      console.log('Envoi de la requête:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('Statut HTTP:', response.status);
      const text = await response.text();
      console.log('Réponse brute:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Données parsées:', data);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        console.log('Activités reçues:', data.activities); // Ajout pour débogage
        setActivities(data.activities || []);
        setFilteredActivities(data.activities || []);
        setError(null);
      } else {
        if (response.status === 403) {
          console.log('Erreur 403: Session expirée');
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/adminlogin');
        } else {
          setError(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur réseau:', error.message);
      setError(`Erreur réseau: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupport = (record) => {
    Modal.confirm({
      title: 'Confirmation de suppression',
      content: `Voulez-vous vraiment supprimer l'enregistrement ID ${record.id} ?`,
      okText: 'Oui',
      okType: 'danger',
      cancelText: 'Non',
      onOk: async () => {
        try {
          const response = await fetch('/backend/admin_delete_support_record.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id: record.id }),
          });
          const data = await response.json();
          if (response.ok) {
            message.success('Enregistrement supprimé avec succès');
            setDataSource(dataSource.filter((item) => item.id !== record.id));
            setFilteredData(filteredData.filter((item) => item.id !== record.id));
          } else {
            message.error(data.error || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur réseau lors de la suppression:', error);
          message.error('Erreur réseau lors de la suppression');
        }
      },
    });
  };

  const handleDeleteActivity = (record) => {
    Modal.confirm({
      title: 'Confirmation de suppression',
      content: `Voulez-vous vraiment supprimer l'activité ID ${record.id} ?`,
      okText: 'Oui',
      okType: 'danger',
      cancelText: 'Non',
      onOk: async () => {
        try {
          const response = await fetch('/backend/admin_delete_service_activity.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id: record.id }),
          });
          const data = await response.json();
          if (response.ok) {
            message.success('Activité supprimée avec succès');
            setActivities(activities.filter((item) => item.id !== record.id));
            setFilteredActivities(filteredActivities.filter((item) => item.id !== record.id));
          } else {
            message.error(data.error || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur réseau lors de la suppression:', error);
          message.error('Erreur réseau lors de la suppression');
        }
      },
    });
  };

  const handleViewSupport = (record) => {
    setSelectedRecord({ type: 'support', data: record });
    setIsModalVisible(true);
  };

  const handleViewActivity = (activity) => {
    setSelectedRecord({ type: 'activity', data: activity });
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  useEffect(() => {
    fetchApprovedRecords();
    fetchServiceActivities();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    const lowerValue = value.toLowerCase();

    // Filtrer les requêtes de support
    const filteredSupports = dataSource.filter((record) =>
      Object.values(record).some((field) =>
        String(field).toLowerCase().includes(lowerValue)
      )
    );
    setFilteredData(filteredSupports);

    // Filtrer les activités de service
    const filteredActivities = activities.filter((activity) =>
      Object.values(activity).some((field) =>
        String(field).toLowerCase().includes(lowerValue)
      ) ||
      (activity.documents && activity.documents.some((doc) =>
        String(doc.description).toLowerCase().includes(lowerValue)
      ))
    );
    setFilteredActivities(filteredActivities);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value) || 10);
  };

  const supportColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Nom', dataIndex: 'name', key: 'name' },
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Code postal', dataIndex: 'zip_code', key: 'zip_code' },
    { title: 'Ville', dataIndex: 'city', key: 'city' },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge
          count={status}
          style={{
            backgroundColor: status === 'Active' ? '#52c41a' : '#ff4d4f',
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            className="action-btn view-btn"
            onClick={() => handleViewSupport(record)}
          />
          <Button
            type="danger"
            icon={<DeleteOutlined />}
            className="action-btn delete-btn"
            onClick={() => handleDeleteSupport(record)}
          />
        </Space>
      ),
      width: 150,
    },
  ];

  const activityColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'activity_date', dataIndex: 'activity_date', key: 'activity_date', width: 120 },
    { title: 'location', dataIndex: 'location', key: 'location', width: 150 },
    { title: 'service_provider', dataIndex: 'service_provider', key: 'service_provider', width: 200 },
    { title: 'primary_staff', dataIndex: 'primary_staff', key: 'primary_staff', width: 150 },
    { title: 'secondary_staff', dataIndex: 'secondary_staff', key: 'secondary_staff', width: 150 },
    { title: 'staff_name', dataIndex: 'staff_name', key: 'staff_name', width: 150 },
    { title: 'activity_type', dataIndex: 'activity_type', key: 'activity_type', width: 120 },
    { title: 'service_delivery', dataIndex: 'service_delivery', key: 'service_delivery', width: 150 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 200 },
    { title: 'created_at', dataIndex: 'created_at', key: 'created_at', width: 150 },
    {
      title: 'Documents',
      key: 'documents',
      render: (_, record) => (
        <Space direction="vertical">
          {record.documents && record.documents.length > 0 ? (
            record.documents.map((doc) => (
              <a
                key={doc.id}
                href={`/backend/${doc.file_path.replace(/^Uploads\//, '')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {doc.description} <DownloadOutlined />
              </a>
            ))
          ) : (
            <Text type="secondary">Aucun document</Text>
          )}
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            className="action-btn view-btn"
            onClick={() => handleViewActivity(record)}
          />
          <Button
            type="danger"
            icon={<DeleteOutlined />}
            className="action-btn delete-btn"
            onClick={() => handleDeleteActivity(record)}
          />
        </Space>
      ),
      width: 150,
    },
  ];

  return (
    <div className="support-requests-container">
      <h2 className="section-title">Gérer les requêtes entrantes</h2>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}
      <div className="table-header">
        <span>
          Afficher <Input defaultValue="10" style={{ width: 60, margin: '0 8px' }} onChange={(e) => handlePageSizeChange(e.target.value)} /> entrées
        </span>
        <Space>
          <Input
            placeholder="Rechercher..."
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
            className="search-input"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <Button
            icon={<ReloadOutlined />}
            type="primary"
            onClick={() => {
              fetchApprovedRecords();
              fetchServiceActivities();
            }}
            loading={loading}
          >
            Rafraîchir
          </Button>
        </Space>
      </div>
      <Table
        columns={supportColumns}
        dataSource={filteredData}
        pagination={{ pageSize, showSizeChanger: false, position: ['bottomRight'] }}
        className="table-style"
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-even' : 'table-row-odd')}
        loading={loading}
      />

      <h2 className="section-title" style={{ marginTop: 40 }}>Mes activités de service</h2>
      {filteredActivities.length > 0 ? (
        <Table
          columns={activityColumns}
          dataSource={filteredActivities}
          pagination={{ pageSize, showSizeChanger: false, position: ['bottomRight'] }}
          scroll={{ x: 'max-content' }}
          className="table-style"
          rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-even' : 'table-row-odd')}
          loading={loading}
        />
      ) : (
        <Alert
          message="Aucune activité trouvée"
          description="Vous n'avez aucune activité de service assignée à votre compte."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      <Modal
        title={selectedRecord?.type === 'support' ? `Détails de l'enregistrement ID ${selectedRecord?.data.id}` : `Détails de l'activité ID ${selectedRecord?.data.id}`}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        okText="Fermer"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {selectedRecord?.type === 'support' && selectedRecord.data && (
          <div>
            <Paragraph><Text strong>Email:</Text> {selectedRecord.data.name}</Paragraph>
            <Paragraph><Text strong>Date:</Text> {selectedRecord.data.date}</Paragraph>
            <Paragraph><Text strong>Code postal:</Text> {selectedRecord.data.zip_code}</Paragraph>
            <Paragraph><Text strong>Ville:</Text> {selectedRecord.data.city}</Paragraph>
            <Paragraph>
              <Text strong>Besoins:</Text>
              {selectedRecord.data.needs.length > 0 ? (
                <ul>
                  {selectedRecord.data.needs.map((need, index) => (
                    <li key={index}>{need}</li>
                  ))}
                </ul>
              ) : (
                ' Aucun'
              )}
            </Paragraph>
            <Paragraph>
              <Text strong>Connexions:</Text>
              {selectedRecord.data.connections.length > 0 ? (
                <ul>
                  {selectedRecord.data.connections.map((connection, index) => (
                    <li key={index}>{connection}</li>
                  ))}
                </ul>
              ) : (
                ' Aucune'
              )}
            </Paragraph>
          </div>
        )}
        {selectedRecord?.type === 'activity' && selectedRecord.data && (
          <div>
            <Paragraph><Text strong>ID:</Text> {selectedRecord.data.id}</Paragraph>
            <Paragraph><Text strong>activity_date:</Text> {selectedRecord.data.activity_date}</Paragraph>
            <Paragraph><Text strong>location:</Text> {selectedRecord.data.location}</Paragraph>
            <Paragraph><Text strong>full_names:</Text> {selectedRecord.data.full_names} ({selectedRecord.data.service_provider})</Paragraph>
            <Paragraph><Text strong>primary_staff:</Text> {selectedRecord.data.primary_staff}</Paragraph>
            <Paragraph><Text strong>secondary_staff:</Text> {selectedRecord.data.secondary_staff || 'N/A'}</Paragraph>
            <Paragraph><Text strong>staff_name:</Text> {selectedRecord.data.staff_name || 'N/A'}</Paragraph>
            <Paragraph><Text strong>activity_type:</Text> {selectedRecord.data.activity_type}</Paragraph>
            <Paragraph><Text strong>service_delivery:</Text> {selectedRecord.data.service_delivery}</Paragraph>
            <Paragraph><Text strong>Notes:</Text> {selectedRecord.data.notes || 'Aucune'}</Paragraph>
            <Paragraph><Text strong>created_at:</Text> {selectedRecord.data.created_at}</Paragraph>
            <Paragraph>
              <Text strong>Documents:</Text>
              {selectedRecord.data.documents && selectedRecord.data.documents.length > 0 ? (
                <ul>
                  {selectedRecord.data.documents.map((doc) => (
                    <li key={doc.id}>
                      <a
                        href={`/backend-docs/${doc.file_path.replace(/^Uploads\//, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.description} <DownloadOutlined />
                      </a>
                      <br />
                      <Text type="secondary">Ajouté le: {doc.created_at}</Text>
                    </li>
                  ))}
                </ul>
              ) : (
                ' Aucun'
              )}
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SupportRequests;